/**
 * nws-high-variant.js
 * -------------------------------------------------------------------------
 * High-variant (250+) support for the custom "free quote" product template.
 *
 * WHY THIS EXISTS
 *   Liquid's `product.variants` is hard-capped at 250 by Shopify, so the old
 *   flat `<select id="product-variant" name="id">` built from that loop could
 *   never see variants past #250 — they were unselectable, unpriced, and could
 *   not be added to cart.
 *
 * WHAT IT DOES
 *   The section now renders one `<select class="nws-hv-option">` per product
 *   option from `product.options_with_values` (which is NOT capped). When the
 *   shopper changes an option we ask the Section Rendering API for the section
 *   rendered with the chosen option-value IDs. Shopify resolves the matching
 *   variant server-side and re-renders `#nws-variant-data-<sectionId>` with that
 *   variant's id / price / compare price / quantity-break tiers. We read those
 *   values and drive the existing price display + quantity-break pricing.
 *
 *   No Storefront API token required; works on whichever store the theme runs on.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  function money(cents) {
    var n = parseInt(cents, 10);
    if (isNaN(n)) return '';
    return '$' + (n / 100).toFixed(2);
  }

  function readQuantity() {
    // The template renders a visible `.quantity__input`; fall back to 1.
    var inputs = document.querySelectorAll('.quantity__input');
    for (var i = 0; i < inputs.length; i++) {
      var v = parseInt(inputs[i].value, 10);
      if (!isNaN(v) && v > 0) return v;
    }
    return 1;
  }

  function NwsHighVariant(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.productUrl = container.dataset.productUrl;
    this.holder = document.getElementById('nws-variant-data-' + this.sectionId);
    this.selects = Array.prototype.slice.call(container.querySelectorAll('.nws-hv-option'));
    this.form = container.closest('form');
    this.abortController = null;

    this.priceEl = document.getElementById('product-price');
    this.compareEl = document.getElementById('compare-price');
    // `#compare-price` is only rendered by Liquid when the initial variant is on
    // sale. Create it (hidden) so a later variant that gains a compare-at price
    // can still show it.
    if (!this.compareEl && this.priceEl && this.priceEl.parentNode) {
      var span = document.createElement('span');
      span.id = 'compare-price';
      span.style.display = 'none';
      this.priceEl.parentNode.appendChild(span);
      this.compareEl = span;
    }
    this.idInput =
      (this.form && this.form.querySelector('input[name="id"].product-variant-id')) ||
      (this.form && this.form.querySelector('input[name="id"]')) ||
      document.querySelector('input[name="id"].product-variant-id');
    this.submitBtn = this.form && this.form.querySelector('button[name="add"]');

    this.bind();
    // Sync price/id with the initially-selected variant + current quantity.
    this.applyPricing();
  }

  NwsHighVariant.prototype.bind = function () {
    var self = this;

    this.selects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        self.onOptionChange();
      });
    });

    // Take over quantity handling. nws-custom.js binds jQuery `.quantity`
    // namespaced handlers that read the now-removed `#product-variant` select;
    // if they ran they would blank out the variant id. Strip them once all the
    // legacy `$(document).ready` callbacks have registered (setTimeout(0)
    // resolves after the ready queue), then own quantity ourselves.
    setTimeout(function () {
      if (window.jQuery) {
        window
          .jQuery(document)
          .off('click.quantity')
          .off('input.quantity')
          .off('change.quantity');
      }

      document.querySelectorAll('.quantity__button').forEach(function (btn) {
        btn.addEventListener('click', function () {
          // Let the theme's own increment/decrement update the field first.
          setTimeout(function () {
            self.applyPricing();
          }, 0);
        });
      });
      document.querySelectorAll('.quantity__input').forEach(function (inp) {
        inp.addEventListener('input', function () {
          self.applyPricing();
        });
        inp.addEventListener('change', function () {
          self.applyPricing();
        });
      });
    }, 0);
  };

  NwsHighVariant.prototype.selectedOptionValueIds = function () {
    // Order by option position so the API receives values in option order.
    return this.selects
      .slice()
      .sort(function (a, b) {
        return (
          parseInt(a.dataset.optionPosition, 10) - parseInt(b.dataset.optionPosition, 10)
        );
      })
      .map(function (sel) {
        var opt = sel.options[sel.selectedIndex];
        return opt ? opt.dataset.optionValueId : '';
      })
      .filter(Boolean);
  };

  NwsHighVariant.prototype.onOptionChange = function () {
    var self = this;
    var ids = this.selectedOptionValueIds();
    if (!ids.length) return;

    var url =
      this.productUrl +
      '?section_id=' +
      encodeURIComponent(this.sectionId) +
      '&option_values=' +
      ids.join(',');

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    fetch(url, { signal: this.abortController.signal })
      .then(function (res) {
        return res.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        self.ingest(doc);
        self.applyPricing();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.error('[nws-high-variant] fetch failed', err);
      });
  };

  // Copy the freshly-resolved variant data + option availability from the
  // fetched section HTML into the live DOM, without disturbing the selection.
  NwsHighVariant.prototype.ingest = function (doc) {
    var newHolder = doc.getElementById('nws-variant-data-' + this.sectionId);
    if (newHolder && this.holder) {
      // Drop stale data-* attributes, then copy the new ones over.
      Array.prototype.slice.call(this.holder.attributes).forEach(function (attr) {
        if (attr.name.indexOf('data-') === 0) this.holder.removeAttribute(attr.name);
      }, this);
      Array.prototype.slice.call(newHolder.attributes).forEach(function (attr) {
        if (attr.name.indexOf('data-') === 0)
          this.holder.setAttribute(attr.name, attr.value);
      }, this);
    }

    // Sync per-value availability (labels + data-unavailable) so unavailable
    // combinations are reflected, while preserving the shopper's selection.
    var fetchedSelects = Array.prototype.slice.call(
      doc.querySelectorAll('.nws-hv-option')
    );
    this.selects.forEach(function (sel) {
      var match = fetchedSelects.filter(function (f) {
        return f.dataset.optionPosition === sel.dataset.optionPosition;
      })[0];
      if (!match) return;
      Array.prototype.slice.call(sel.options).forEach(function (opt) {
        var fOpt = Array.prototype.slice.call(match.options).filter(function (o) {
          return o.dataset.optionValueId === opt.dataset.optionValueId;
        })[0];
        if (!fOpt) return;
        if (fOpt.dataset.unavailable) {
          opt.setAttribute('data-unavailable', 'true');
        } else {
          opt.removeAttribute('data-unavailable');
        }
      });
    });
  };

  NwsHighVariant.prototype.applyPricing = function () {
    if (!this.holder) return;
    var holder = this.holder;
    // Read via getAttribute (not dataset): attributes whose name starts with a
    // digit — data-2qty, data-11qty, data-26qty — are not reliably exposed on
    // element.dataset across browsers.
    var attr = function (name) {
      return holder.getAttribute('data-' + name);
    };
    var qty = readQuantity();

    var id = attr('variant-id');
    var price = attr('price');
    var cprice = attr('cprice');

    // Quantity-break tiers reference sibling variants (via metafields). Pick the
    // highest tier the current quantity qualifies for — mirrors the legacy logic.
    if (qty >= 26 && attr('26qty')) {
      id = attr('26qty');
      price = attr('26price');
      cprice = attr('26cprice');
    } else if (qty >= 11 && attr('11qty')) {
      id = attr('11qty');
      price = attr('11price');
      cprice = attr('11cprice');
    } else if (qty >= 3 && attr('3qty')) {
      id = attr('3qty');
      price = attr('3price');
      cprice = attr('3cprice');
    } else if (qty >= 2 && attr('2qty')) {
      id = attr('2qty');
      price = attr('2price');
      cprice = attr('2cprice');
    }

    var available = attr('available') !== 'false' && !!id;

    // Update the variant id that will be submitted to /cart/add.
    if (this.idInput) this.idInput.value = available ? id : '';

    // Price display.
    if (this.priceEl && price) this.priceEl.textContent = money(price);
    if (this.compareEl) {
      if (cprice && parseInt(cprice, 10) > parseInt(price, 10)) {
        this.compareEl.textContent = money(cprice);
        this.compareEl.style.display = '';
      } else {
        this.compareEl.style.display = 'none';
      }
    }

    // Enable/disable add-to-cart for unavailable combinations.
    if (this.submitBtn) {
      this.submitBtn.disabled = !available;
      this.submitBtn.setAttribute('aria-disabled', String(!available));
    }
  };

  function init() {
    var container = document.querySelector('[data-nws-hv]');
    if (!container) return; // Only the high-variant template opts in.
    new NwsHighVariant(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
